// ============================================================================
// « ÉTIQUETTES » — les deux onglets du module (maquette `isLabels`, relevé 2026-08-26).
//
// ⚠️ L'IMPRESSION DE MASSE EST LE PREMIER ONGLET, ET CELUI PAR DÉFAUT — comme la maquette
// (`lbTab` vaut `print`). On règle un gabarit une fois, on tire des étiquettes toutes les semaines :
// ouvrir sur l'éditeur ferait traverser un écran de réglages pour atteindre le geste courant.
//
// ⚠️ UN STANDARD NE SE MODIFIE PAS ET NE SE DÉTRUIT PAS. C'est la règle centrale du module, et elle
// n'est pas gardée par un `disabled` : les quatre gabarits livrés sont des CONSTANTES DE CODE
// (`@orgazone/core-catalog`) — il n'y a rien à modifier. Les retirer les MASQUE, et l'écran le
// promet en toutes lettres, donc le magasin peut les rétablir.
//
// ⚠️ IL N'Y A AUCUN BOUTON « ENREGISTRER », ET IL NE FAUT PAS EN AJOUTER. La maquette pose
// l'enregistrement automatique comme un fait dit à l'écran. Mais « enregistré » n'est affiché
// qu'APRÈS confirmation du serveur (§23.0) : pendant la frappe on dit « enregistrement… », et un
// échec dit son motif RÉEL au lieu de laisser croire que le travail est en sûreté.
//
// ⚠️ L'APERÇU PORTE UN VRAI ARTICLE DU MAGASIN. Régler une boîte de désignation sur un échantillon
// de démonstration ne dit rien des noms réels de ce catalogue-là — or c'est la seule question qu'on
// se pose en réglant un gabarit. Catalogue vide → on le dit, on n'invente pas de « Chemise Oxford ».
//
// ⚠️ LA MISE À L'ÉCHELLE DU NAVIGATEUR EST LE VRAI RISQUE À L'IMPRESSION, pas le format. Un pilote
// réglé sur « ajuster à la page » anéantit tout le travail au millimètre — et le code-barres avec.
// Ça se DIT à l'écran ; ça ne se corrige pas en code.
// ============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import {
  availableFields, canRemoveTemplate, clampPosition, clampSize, EDITOR_ZOOMS, elementWidthMm, estimateTextWidthMm,
  findCatalogFormat, fitSheet, formatLabel, LABEL_FORMAT_CATALOG,
  LABEL_MAX_HEIGHT_MM, LABEL_MAX_WIDTH_MM, LABEL_MIN_MM, mediaLabel, newElementFor, newTemplate,
  snapMm, visibleTemplates,
  type LabelTemplate,
} from '@orgazone/core-catalog';
import { timeSec } from '@orgazone/date-format';
import { alert, Button, confirm, EmptyState, Field, FieldGroup, Icon, Modal, Segmented, showToast, Tabs, Trans, type IconName } from '@orgazone/ui-web';
import { api, type LabelSampleRow } from '../api';
import { isEmptyOnLabel, LabelPlate, labelValuesOf, type LabelValues } from './etiquettes-render';
import { ElementPanel, labelFieldName, MmField } from './etiquettes-panel';
import { PrintTunnel } from './etiquettes-tunnel';
import { buildPrintDocument, openPrintDocument } from './etiquettes-print';
import { t } from '../i18n';

/** L'état d'enregistrement — ⚠️ « enregistré » n'apparaît qu'après confirmation du serveur. */
type SaveState = { kind: 'idle' } | { kind: 'saving' } | { kind: 'saved'; at: string } | { kind: 'failed'; reason: string };

export function Etiquettes() {
  const [custom, setCustom] = useState<LabelTemplate[]>([]);
  const [hidden, setHidden] = useState<string[]>([]);
  const [standard, setStandard] = useState<LabelTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedKey, setSelectedKey] = useState<string>('');
  const [selectedElement, setSelectedElement] = useState<number | null>(null);
  const [zoom, setZoom] = useState<number>(6);
  const [snap, setSnap] = useState(true);
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  const [formatOpen, setFormatOpen] = useState(false);
  // ⚠️ L'IMPRESSION EST L'ONGLET PAR DÉFAUT, comme dans la maquette (`lbTab` vaut `'print'`), et
  //    c'est le bon ordre : on règle un gabarit une fois, on tire des étiquettes toutes les semaines.
  //    Ouvrir sur l'éditeur ferait traverser un écran de réglages pour atteindre le geste courant.
  const [tab, setTab] = useState<'edit' | 'print'>('print');

  const [testCopies, setTestCopies] = useState(1);

  const [sampleIndex, setSampleIndex] = useState(0);
  const [sample, setSample] = useState<LabelSampleRow | null>(null);
  const [sampleTotal, setSampleTotal] = useState<number | null>(null);
  const [sampleError, setSampleError] = useState<string | null>(null);
  /** Une nouvelle tentative de lecture de l'aperçu — le même index relu après un échec. */
  const [sampleAttempt, setSampleAttempt] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.labels();
      setStandard(d.standard);
      setCustom(d.custom);
      setHidden(d.hidden);
      setLoadError(null);
      setSelectedKey((k) => k || visibleTemplates(d.custom, d.hidden)[0]?.key || '');
    } catch (e) {
      // ⚠️ LE MOTIF RÉEL, jamais « une erreur est survenue » : c'est la seule chose qui permette de
      //    savoir s'il faut choisir un magasin, prévenir quelqu'un, ou réessayer.
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // L'article d'aperçu — rechargé à chaque « Autre article ».
  useEffect(() => {
    let cancelled = false;
    api.labelSample(sampleIndex)
      .then((d) => { if (!cancelled) { setSample(d.row); setSampleTotal(d.total); setSampleError(null); } })
      // ⚠️ BEST-EFFORT JUSTIFIÉ : sans échantillon, la planche se dessine avec des champs vides, et l'éditeur reste
      //    utilisable — le faire échouer en entier pour un exemple manquant empêcherait de régler un gabarit.
      //    ⚠️ MAIS L'ÉCHEC SE DIT AVEC SON MOTIF : le confondre avec un catalogue vide (« aucun article ») ferait
      //    affirmer au magasin qu'il n'a rien, alors qu'on n'a simplement pas pu lire.
      .catch((e: unknown) => {
        if (!cancelled) { setSample(null); setSampleTotal(null); setSampleError(e instanceof Error ? e.message : String(e)); }
      });
    return () => { cancelled = true; };
  }, [sampleIndex, sampleAttempt]);

  const shown = visibleTemplates(custom, hidden);
  const template = shown.find((tpl) => tpl.key === selectedKey) ?? shown[0] ?? null;
  const locked = template?.locked ?? true;

  // ── L'ENREGISTREMENT AUTOMATIQUE ───────────────────────────────────────────────────────────────
  // ⚠️ DEUX CHOSES DISTINCTES, et les confondre serait mentir : la planche suit le curseur TOUT DE
  //    SUITE (état d'édition local, aucune promesse), et la ligne « Enregistré à HH:MM:SS » n'est
  //    posée qu'après le 200 du serveur. Un échec dit son motif et n'efface pas le travail à l'écran.
  const pending = useRef<LabelTemplate | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ⚠️ L'ÉCRAN NE PARLE PLUS APRÈS SON DÉMONTAGE : le dernier envoi part quand on quitte la page,
  //    mais poser un état sur un composant disparu n'informerait personne et salirait la console.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  /**
   * ⚠️ UN SEUL ENVOI EN VOL À LA FOIS. Le serveur traite deux requêtes dans l'ordre qu'il veut : deux envois croisés
   *    pouvaient laisser en base l'ANCIEN gabarit sous « Enregistré », et la réponse de l'ancien réécrivait à l'écran un
   *    état dépassé — le geste suivant partait alors de lui, et perdait le précédent. Un geste arrivé pendant l'envoi
   *    attend son tour ; c'est la fin de l'envoi en cours qui le fait partir.
   */
  const inFlight = useRef(false);
  const flush = useCallback(async (): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      while (pending.current) {
        const next = pending.current;
        pending.current = null;
        try {
          const saved = await api.saveLabelTemplate(next);
          // ⚠️ UNE RÉPONSE DÉPASSÉE NE TOUCHE PAS À L'ÉCRAN : un geste plus récent y est déjà, et c'est lui qui part ensuite.
          if (mounted.current && !pending.current) {
            setCustom((list) => list.map((tpl) => (tpl.key === saved.key ? saved : tpl)));
            setSave({ kind: 'saved', at: timeSec(new Date()) });
          }
        } catch (e) {
          if (mounted.current) setSave({ kind: 'failed', reason: e instanceof Error ? e.message : String(e) });
        }
        // Un geste dont le délai court encore partira à son terme : on ne le devance pas. Celui dont le délai est échu
        // pendant l'envoi part maintenant — y compris après le démontage, où plus rien d'autre ne l'enverrait.
        if (timer.current) break;
      }
    } finally {
      inFlight.current = false;
    }
  }, []);

  /**
   * Applique une modification : à l'écran maintenant, au serveur juste après.
   * ⚠️ L'ÉTAT PASSE À « EN COURS » DÈS LE GESTE, pas au départ de la requête. Sans ça, l'écran
   *    continuerait d'afficher « Enregistré à 10:03:12 » pendant toute l'attente du débounce, alors
   *    qu'une modification plus récente n'est nulle part — la confirmation désignerait un état périmé.
   */
  const mutate = useCallback((next: LabelTemplate) => {
    setCustom((list) => list.map((tpl) => (tpl.key === next.key ? next : tpl)));
    pending.current = next;
    setSave({ kind: 'saving' });
    if (timer.current) clearTimeout(timer.current);
    // ⚠️ LE DÉLAI SE MARQUE ÉCHU (`null`) EN PARTANT : c'est ce qui dit à un envoi en cours qu'il doit enchaîner.
    timer.current = setTimeout(() => { timer.current = null; void flush(); }, 500);
  }, [flush]);

  // ⚠️ ON VIDE LA FILE EN QUITTANT L'ÉCRAN : sans ça, le dernier geste avant de changer de page
  //    resterait dans un `setTimeout` que le démontage emporte — perdu, et l'écran aurait dit
  //    « enregistrement… » jusqu'au bout.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    void flush();
  }, [flush]);

  const patchElement = useCallback((index: number, patch: Partial<LabelTemplate['elements'][number]>) => {
    if (!template || template.locked) return;
    mutate({ ...template, elements: template.elements.map((e, i) => (i === index ? { ...e, ...patch } : e)) });
  }, [template, mutate]);

  // ── LE GLISSER ─────────────────────────────────────────────────────────────────────────────────
  // ⚠️ `moved` : un geste ne s'applique qu'une fois le pointeur parti au-delà du seuil (`beyondDragThreshold`).
  const drag = useRef<{ index: number; x0: number; y0: number; ex: number; ey: number; moved: boolean } | null>(null);
  const resize = useRef<{ index: number; x0: number; y0: number; w: number; s: number; moved: boolean } | null>(null);

  const onGrab = useCallback((index: number, e: ReactPointerEvent<HTMLDivElement>) => {
    setSelectedElement(index);
    if (!template || template.locked) return;
    const el = template.elements[index];
    if (!el) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* capture indisponible : le glisser marche quand même, en moins fluide */ }
    drag.current = { index, x0: e.clientX, y0: e.clientY, ex: el.xMm, ey: el.yMm, moved: false };
  }, [template]);

  const onResizeGrab = useCallback((index: number, e: ReactPointerEvent<HTMLSpanElement>) => {
    if (!template || template.locked) return;
    const el = template.elements[index];
    if (!el) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* idem */ }
    const w = startWidthMm(el, e.currentTarget.parentElement, zoom);
    resize.current = { index, x0: e.clientX, y0: e.clientY, w, s: el.sizeMm, moved: false };
  }, [template, zoom]);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!template) return;
    const d = drag.current;
    if (d) {
      if (!d.moved && !beyondDragThreshold(e.clientX - d.x0, e.clientY - d.y0)) return;
      d.moved = true;
      const p = clampPosition(template, snapMm(d.ex + (e.clientX - d.x0) / zoom, snap), snapMm(d.ey + (e.clientY - d.y0) / zoom, snap));
      patchElement(d.index, { xMm: p.xMm, yMm: p.yMm });
      return;
    }
    const r = resize.current;
    if (r) {
      const el = template.elements[r.index];
      if (!el) return;
      if (!r.moved && !beyondDragThreshold(e.clientX - r.x0, e.clientY - r.y0)) return;
      r.moved = true;
      const s = clampSize(template, el.field, snapMm(r.w + (e.clientX - r.x0) / zoom, snap), snapMm(r.s + (e.clientY - r.y0) / zoom, snap));
      patchElement(r.index, { widthMm: s.widthMm, sizeMm: s.sizeMm });
    }
  }, [template, zoom, snap, patchElement]);

  const onPointerUp = useCallback(() => { drag.current = null; resize.current = null; }, []);

  // ── LES GESTES SUR LE GABARIT ──────────────────────────────────────────────────────────────────
  const createTemplate = useCallback(async () => {
    let n = 1;
    let key = 'perso';
    while (custom.some((tpl) => tpl.key === key)) { n += 1; key = `perso${n}`; }
    // ⚠️ LE NOM EST UNE DONNÉE ENREGISTRÉE, écrite dans la langue de celui qui crée — puis renommable. Deux phrases
    //    entières plutôt qu'un numéro collé en valeur : chaque langue place le sien où sa grammaire le veut.
    const draft = newTemplate(key, n > 1 ? t('Étiquette personnalisée {v}', { v: n }) : t('Étiquette personnalisée'));
    try {
      const saved = await api.saveLabelTemplate(draft);
      setCustom((list) => [saved, ...list]);
      setSelectedKey(saved.key);
      setSelectedElement(null);
      setSave({ kind: 'saved', at: timeSec(new Date()) });
    } catch (e) {
      void alert({ title: t('Le gabarit n’a pas pu être créé'), message: e instanceof Error ? e.message : String(e) });
    }
  }, [custom]);

  const removeTemplate = useCallback(async (tpl: LabelTemplate) => {
    // ⚠️ LA GARDE EST MESURÉE ICI *ET* AU SERVEUR : ici pour ne pas ouvrir une modale sur un geste
    //    voué au refus, au serveur parce que c'est lui qui décide.
    if (!canRemoveTemplate(custom, hidden, tpl.key)) {
      void alert({ title: t('Ce gabarit ne peut pas être retiré'), message: t('C’est le dernier gabarit visible : le retirer laisserait le magasin sans aucune étiquette à imprimer.') });
      return;
    }
    const okToGo = await confirm({
      title: tpl.locked ? t('Masquer « {name} » ?', { name: tpl.name }) : t('Supprimer « {name} » ?', { name: tpl.name }),
      message: tpl.locked
        ? t('Il sera masqué, pas détruit — vous pourrez le rétablir à tout moment.')
        : t('Cette disposition sera perdue. L’opération est irréversible.'),
      confirmLabel: tpl.locked ? t('Masquer') : t('Supprimer'),
      tone: tpl.locked ? 'info' : 'danger',
    });
    if (!okToGo) return;
    try {
      // ⚠️ C'EST LE SERVEUR QUI DIT CE QU'IL A FAIT (masqué ou supprimé) : la nature du gabarit décide chez lui, et
      //    l'écran n'en garde pas une seconde copie.
      const done = await api.removeLabelTemplate(tpl.key);
      if (done.hidden) setHidden((h) => (h.includes(tpl.key) ? h : [...h, tpl.key]));
      else setCustom((list) => list.filter((x) => x.key !== tpl.key));
      // ⚠️ LE GABARIT RETIRÉ ÉTAIT PEUT-ÊTRE CELUI QU'ON ÉDITAIT : sans ce repli, la planche se
      //    viderait sans rien dire et l'écran semblerait cassé.
      if (selectedKey === tpl.key) setSelectedKey('');
    } catch (e) {
      void alert({
        title: tpl.locked ? t('Le gabarit n’a pas pu être masqué') : t('Le gabarit n’a pas pu être supprimé'),
        message: e instanceof Error ? e.message : String(e),
      });
    }
  }, [custom, hidden, selectedKey]);

  const restoreTemplate = useCallback(async (key: string) => {
    try {
      await api.restoreLabelTemplate(key);
      setHidden((h) => h.filter((k) => k !== key));
    } catch (e) {
      void alert({ title: t('Le gabarit n’a pas pu être rétabli'), message: e instanceof Error ? e.message : String(e) });
    }
  }, []);

  // ── CE QUE LA PLANCHE MONTRE ───────────────────────────────────────────────────────────────────
  // ⚠️ AUCUNE VALEUR INVENTÉE : sans article, tous les champs sont vides et l'écran le dit.
  // ⚠️ MÉMOÏSÉ parce que le tirage d'essai en DÉPEND : recomposé à chaque rendu, il redonnerait une
  //    identité neuve à chaque frappe et rendrait ce rappel inutile — ce que le linter attrape.
  const values: LabelValues = useMemo(() => (sample ? labelValuesOf(sample) : {}), [sample]);

  /**
   * LE TIRAGE D'ESSAI — l'article de l'aperçu, en N exemplaires, par le MÊME générateur que la série.
   *
   * ⚠️ LE MÊME GÉNÉRATEUR, ET C'EST TOUT L'INTÉRÊT : un essai produit par un autre chemin
   * validerait un calage que l'impression réelle ne reproduirait pas — on gâcherait la planche en
   * ayant fait exactement ce qu'il fallait pour l'éviter.
   * ⚠️ SANS ARTICLE, ON NE FAIT RIEN (le bouton est d'ailleurs indisponible) : imprimer des champs
   * vides gâcherait du papier pour ne rien montrer.
   */
  const printTest = useCallback(() => {
    if (!template || !sample) return;
    const outcome = openPrintDocument(buildPrintDocument(template, [{ values, copies: testCopies }]));
    if (!outcome.ok) {
      // ⚠️ UN ÉCHEC D'OUVERTURE SE DIT : sinon on attend devant une imprimante silencieuse.
      void alert({ title: t('La page d’impression n’a pas pu s’ouvrir'), message: outcome.reason });
    }
  }, [template, sample, values, testCopies]);

  // ⚠️ AUCUN PADDING DANS LES ÉTATS NON PLUS : le `<main>` du shell en pose déjà un, et un second
  //    s'y ajoute. Les autres écrans rendent leurs états nus, pour cette raison exactement.
  if (loading) return <div style={{ color: 'var(--ink-500)' }}>{t('Chargement des gabarits…')}</div>;
  if (loadError) return <EmptyState icon="tag" title={t('Les gabarits n’ont pas pu être lus')} note={loadError} />;

  const hiddenTemplates = standard.filter((tpl) => hidden.includes(tpl.key));
  const element = template && selectedElement !== null ? template.elements[selectedElement] : undefined;
  // Les champs sans valeur sur l'article d'aperçu — affiché au pied seulement quand un article est lu : sans lui, le pied
  // dit pourquoi l'aperçu est vide, et tout se montre en fantôme.
  const emptyCount = template ? template.elements.filter((e) => isEmptyOnLabel(e, values)).length : 0;

  return (
    // ⚠️ AUCUN PADDING À LA RACINE : le `<main>` du shell porte déjà `20px 24px 40px` (App.tsx), et
    //    un second s'y AJOUTE — cet écran affichait le double partout. Les seize autres écrans
    //    routés s'appuient sur le shell ; celui-ci était le seul à en poser un (audit 2026-08-27).
    <div>
      {/* La rangée d'en-tête COMMUNE : titre + sous-titre à gauche, actions à droite quand il y en
          a. Même géométrie que Promotions, Écarts, Clôture, Articles rapides et Référentiels — c'est
          elle qui donne aux pages leur rythme identique, pas une valeur recopiée au jugé. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Étiquettes')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
            {/* ⚠️ LE COMPTE EST RÉEL (les gabarits visibles de CE magasin), jamais un chiffre d'ambiance. */}
            {t('{n} gabarit disponible — déplacez chaque élément à la position exacte que votre imprimante attend.', { n: shown.length })}
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Tabs
        items={[{ id: 'print', label: t('Impression de masse') }, { id: 'edit', label: t('Gabarits') }]}
        value={tab}
        onChange={setTab}
        panelId="etiquettes-panneau"
        label={t('Sections du module Étiquettes')}
      />

      {/* ⚠️ LE PANNEAU EST UN `tabpanel` NOMMÉ PAR SON ONGLET : les onglets le désignent (`aria-controls`), une aide
          technique annonce donc « panneau Gabarits » en y entrant. Pas de `display: contents` — un élément qui en porte
          perd son rôle dans l'arbre d'accessibilité de plusieurs navigateurs. */}
      {tab === 'print' && (
        <div id="etiquettes-panneau" role="tabpanel" aria-labelledby="etiquettes-panneau-tab-print">
          {/* ⚠️ LE TUNNEL REÇOIT LES GABARITS VISIBLES, il n'en relit aucun : deux lectures de la même
              liste finiraient par proposer à l'impression un gabarit que l'éditeur vient de masquer. */}
          <PrintTunnel templates={shown} />
        </div>
      )}

      {tab === 'edit' && <div id="etiquettes-panneau" role="tabpanel" aria-labelledby="etiquettes-panneau-tab-edit"
        style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* ── LA BANDE DE SÉLECTION ──────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: 12 }}>
        {shown.map((tpl) => (
          <TemplateCard
            key={tpl.key}
            template={tpl}
            values={values}
            active={tpl.key === template?.key}
            onPick={() => { setSelectedKey(tpl.key); setSelectedElement(null); }}
            onRemove={() => { void removeTemplate(tpl); }}
          />
        ))}
        <button
          type="button"
          onClick={() => { void createTemplate(); }}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 9,
            padding: '14px 15px', border: '1.5px dashed var(--ink-300)', borderRadius: 'var(--r-sm)',
            background: 'var(--ink-50)', minHeight: 118, cursor: 'pointer',
          }}
        >
          <Icon name="plus" size={18} />
          <span style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Nouvelle étiquette')}</span>
          <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', textAlign: 'center', lineHeight: 1.4 }}>
            {t('Format et disposition entièrement libres')}
          </span>
        </button>
      </div>

      {/* ── LES GABARITS MASQUÉS — la promesse tenue ───────────────────────────────────────────── */}
      {hiddenTemplates.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>{t('Gabarits masqués —')}</span>
          {hiddenTemplates.map((tpl) => (
            <Button key={tpl.key} size="sm" icon="returnBox" aria-label={t('Rétablir « {name} »', { name: tpl.name })}
              onClick={() => { void restoreTemplate(tpl.key); }}>
              {tpl.name}
            </Button>
          ))}
        </div>
      )}
      {!template ? (
        <EmptyState icon="tag" title={t('Aucun gabarit')} note={t('Créez une étiquette pour commencer.')} />
      ) : (
        <>
          {/* ⚠️ LES DEUX CARTES SONT CELLES DE LA MAQUETTE : la planche et tout ce qui la décrit dans
              UNE carte, les propriétés de l'élément dans une seconde. Une seule surface pour « le
              gabarit » et une pour « l'élément sélectionné » — c'est ce découpage-là qui fait qu'on
              sait, sans réfléchir, sur quoi agit le contrôle qu'on touche. */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>

            <section style={{ ...CARD, flex: '1 1 420px', minWidth: 0 }}>
              {/* L'en-tête : ce qu'on édite, et comment on le regarde. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 11, flexWrap: 'wrap' }}>
                <span style={{ flex: 1, minWidth: 120, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>
                  {template.name}
                </span>
                <Segmented
                  label={t('Zoom')}
                  options={EDITOR_ZOOMS.map((z) => ({ id: String(z), label: `${z}×` }))}
                  value={String(zoom)}
                  onChange={(v) => setZoom(Number(v))}
                  size="sm"
                />
                <Button size="sm" icon={snap ? 'check' : 'grid'} onClick={() => setSnap((s) => !s)}>
                  {snap ? t('Grille 0,5 mm') : t('Placement libre')}
                </Button>
              </div>

              {/* ⚠️ LA RÈGLE CENTRALE DU MODULE MÉRITE D'ÊTRE VUE. En gris sur blanc, elle se lisait
                  comme une légende ; en bleu, elle se lit comme un ÉTAT de l'objet. Bleu et pas
                  ambre : un gabarit standard verrouillé n'est pas un problème à corriger, c'est une
                  propriété — et l'ambre est réservé à ce qui va mal (retour utilisateur 2026-08-27). */}
              {locked ? (
                <Notice tone="info" icon="lock">
                  <Trans k='<1>Gabarit standard, non modifiable.</1> Format et disposition sont figés pour garantir une étiquette conforme sur tout le parc. Créez une <2>Nouvelle étiquette</2> pour un gabarit sur mesure.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></Notice>
              ) : (
                // ⚠️ UN PANNEAU PAR GABARIT (`key`) : ses champs ne sont pas contrôlés, et passer d'un gabarit à l'autre
                //    laissait dans « Nom » celui du précédent — le quitter renommait le nouveau gabarit.
                <FormatPanel
                  key={template.key}
                  template={template}
                  onOpenCatalog={() => setFormatOpen(true)}
                  onChange={mutate}
                />
              )}

              <div
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                style={{
                  display: 'flex', justifyContent: 'center', padding: 22,
                  background: 'var(--ink-50)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)',
                  overflow: 'auto',
                }}
              >
                <LabelPlate
                  template={template}
                  values={values}
                  scale={zoom}
                  selected={selectedElement}
                  interaction={{ onGrab, onSelect: setSelectedElement, nameOf: labelFieldName }}
                  onResize={locked ? undefined : onResizeGrab}
                  onBlank={() => setSelectedElement(null)}
                  style={{ boxShadow: '0 1px 6px rgba(12,14,16,.12)' }}
                />
              </div>

              {!locked && <SaveLine state={save} />}

              {/* Le pied de planche : le format tiré, et l'article sur lequel on le vérifie. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{mediaLabel(template)}</span>
                <span style={{ color: 'var(--ink-300)' }}>·</span>
                {sample ? (
                  <span style={{ flex: 1, minWidth: 120, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
                    
                    <Trans k='Aperçu sur <1>{v}</1>' vars={{ v: sample.desig || sample.gencode }} parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
                    {emptyCount > 0 && (
                      <>
                        <span style={{ color: 'var(--ink-300)', margin: '0 6px' }}>·</span>
                        {t('{n} champ vide', { n: emptyCount })}
                      </>
                    )}
                  </span>
                ) : (
                  // ⚠️ ON LE DIT PLUTÔT QUE DE MEUBLER : une planche remplie de faux textes laisserait
                  //    croire que les boîtes sont réglées pour ce catalogue-là.
                  <span style={{ flex: 1, minWidth: 120, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
                    {sampleError !== null
                      ? t('L’article d’aperçu n’a pas pu être lu — {motif}', { motif: sampleError })
                      : sampleTotal === 0 ? t('Aucun article à code-barres dans ce magasin — l’aperçu reste vide.') : t('Aperçu en attente…')}
                  </span>
                )}
                {(sampleTotal ?? 0) > 1 && (
                  <Button size="sm" onClick={() => setSampleIndex((i) => i + 1)}>{t('Autre article')}</Button>
                )}
                {sampleError !== null && (
                  <Button size="sm" icon="refresh" onClick={() => setSampleAttempt((n) => n + 1)}>{t('Réessayer')}</Button>
                )}
              </div>
            </section>

            {/* ── LE PANNEAU DE PROPRIÉTÉS ────────────────────────────────────────────────────── */}
            <aside style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: '0 1 320px', minWidth: 260 }}>
              {element && selectedElement !== null ? (
                <ElementPanel
                  template={template}
                  index={selectedElement}
                  locked={locked}
                  snap={snap}
                  sampleText={values[element.field] ?? ''}
                  emptyOnSample={!!sample && isEmptyOnLabel(element, values)}
                  onPatch={(patch) => patchElement(selectedElement, patch)}
                  onRemove={() => {
                    mutate({ ...template, elements: template.elements.filter((_, i) => i !== selectedElement) });
                    setSelectedElement(null);
                  }}
                />
              ) : (
                <div style={CARD}>
                  <div style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Choisissez un élément')}</div>
                  <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
                    
                    {t('Cliquez un champ sur la planche pour régler sa position, sa taille et son alignement.')}
                  </div>
                </div>
              )}

              {!locked && availableFields(template).length > 0 && (
                <div style={CARD}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
                    
                    {t('Ajouter un élément')}
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {availableFields(template).map((f) => (
                      <Button
                        key={f}
                        size="sm"
                        icon="plus"
                        onClick={() => {
                          const next = { ...template, elements: [...template.elements, newElementFor(template, f)] };
                          mutate(next);
                          setSelectedElement(next.elements.length - 1);
                        }}
                      >
                        {labelFieldName(f)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>

          {/* ── LE TIRAGE D'ESSAI — sa propre carte, comme dans la maquette ──────────────────────
              ⚠️ IL EST À PART, ET C'EST JUSTE : régler un gabarit et vérifier qu'il sort bien de
              l'imprimante sont deux gestes. Le mêler à la planche ferait cliquer « imprimer » en
              croyant enregistrer. */}
          <section style={{ ...CARD, flexDirection: 'row', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 210 }}>
              <div style={{ fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Imprimer une série d’essai')}</div>
              <p style={{ margin: '3px 0 0', fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
                {/* ⚠️ CE QUE ÇA IMPRIME VRAIMENT : l'article de l'aperçu, en N exemplaires. La maquette
                    annonce ici « sélectionnez des articles dans la liste » — ce n'est pas ce que fait
                    le bouton, et un libellé qui décrit une autre action est un écran qui ment (§23.0). */}
                
                {t('Un tirage sur l’article de l’aperçu, pour vérifier le calage de votre imprimante avant de lancer une série. Les étiquettes de masse se tirent depuis l’onglet « Impression de masse ».')}
              </p>
            </div>
            <label style={{ flexShrink: 0 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 4 }}>
                
                {t('Exemplaires')}
              </span>
              <input
                type="number"
                min={1}
                max={TEST_PRINT_MAX}
                inputMode="numeric"
                aria-label={t('Nombre d’exemplaires du tirage d’essai')}
                value={String(testCopies)}
                onChange={(e) => setTestCopies(Math.max(1, Math.min(TEST_PRINT_MAX, Number.parseInt(e.target.value, 10) || 1)))}
                style={{ width: 110, height: 34, padding: '0 9px', textAlign: 'right', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontFamily: 'var(--font-mono)' }}
              />
            </label>
            {/* ⚠️ SANS ARTICLE, PAS DE TIRAGE : imprimer une étiquette aux champs vides gâcherait du
                papier pour ne rien montrer, et laisserait croire que le gabarit est fautif. */}
            <Button variant="primary" icon="printer" disabled={!sample} onClick={printTest}>
              
              {t('Imprimer un test')}
            </Button>
          </section>

          <div style={{ padding: '14px 16px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.6 }}>
            <Trans k='Les positions sont en <1>millimètres depuis le coin haut-gauche</1> de l’étiquette, jamais en pixels : une imprimante thermique raisonne en millimètres, et le gabarit doit rester juste quelle que soit sa résolution. La grille de 0,5 mm aide à aligner ; désactivez-la pour un placement au dixième.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
        </>
      )}

      </div>}
      </div>

      {formatOpen && template && !template.locked && (
        <FormatCatalog
          template={template}
          onClose={() => setFormatOpen(false)}
          onPick={(widthMm, heightMm, kind) => {
            const fit = fitSheet(widthMm, heightMm);
            mutate({ ...template, widthMm, heightMm, kind, cols: fit.cols, rows: fit.rows });
            setFormatOpen(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Un encart de consigne — le même habillage pour tout ce que l'écran doit DIRE.
 *
 * ⚠️ TROIS TONS, ET LA DIFFÉRENCE EST SÉMANTIQUE, jamais décorative :
 *   · `info` = un ÉTAT de l'objet, en BLEU. Un gabarit standard verrouillé n'est pas un problème à
 *     corriger : c'est une propriété, et l'annoncer en ambre ferait chercher une faute qui n'existe pas.
 *   · `warn` = quelque chose ne va pas et se corrige (ambre).
 *   · `quiet` = une note de bas de page, qui n'appelle aucune action (gris).
 * ⚠️ Le bleu vient de la maquette elle-même (`--blue-bg` + bordure `--blue`), qui l'emploie déjà pour
 * ses encarts informatifs : ce n'est pas une couleur inventée pour cet écran.
 */
function Notice({ tone, icon, children }: { tone: 'info' | 'warn' | 'quiet'; icon?: IconName; children: React.ReactNode }) {
  const palette = tone === 'warn'
    ? { bg: 'var(--amber-bg)', border: 'var(--amber)', mark: 'var(--amber-fg)' }
    : tone === 'info'
      ? { bg: 'var(--blue-bg)', border: 'var(--blue)', mark: 'var(--blue-fg)' }
      : { bg: 'var(--ink-50)', border: 'var(--ink-200)', mark: 'var(--ink-500)' };
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 11, padding: '13px 15px',
      borderRadius: 'var(--r-sm)', background: palette.bg, border: `1px solid ${palette.border}`,
    }}>
      <span style={{ color: palette.mark, flexShrink: 0, display: 'flex', paddingTop: 1 }}>
        <Icon name={icon ?? (tone === 'warn' ? 'alert' : 'info')} size={17} />
      </span>
      <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-body)', color: 'var(--ink-700)', lineHeight: 1.55 }}>{children}</span>
    </div>
  );
}

/** La carte de l'écran — la MÊME surface pour la planche, les propriétés et le tirage d'essai. */
const CARD = {
  display: 'flex' as const, flexDirection: 'column' as const, gap: 14, padding: 18,
  background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)',
};

/** ⚠️ Plafond du tirage d'essai : au-delà ce n'est plus un essai, c'est une série (maquette : 99). */
const TEST_PRINT_MAX = 99;

/**
 * CE QUE L'ENREGISTREMENT AUTOMATIQUE A RÉELLEMENT FAIT.
 * ⚠️ « Enregistré » n'apparaît qu'après le 200 du serveur, et un échec dit son motif : c'est la
 * différence entre un écran qui rassure et un écran qui informe (§23.0).
 */
function SaveLine({ state }: { state: SaveState }) {
  if (state.kind === 'failed') {
    return (
      <Notice tone="warn">
        <Trans k='<1>Cette modification n’a pas été enregistrée.</1> {reason} — votre travail est toujours à l’écran ; refaites un geste pour réessayer.' vars={{ reason: state.reason }} parts={[(x) => <strong>{x}</strong>]} /></Notice>
    );
  }
  return (
    <Notice tone="quiet" icon={state.kind === 'saving' ? 'refresh' : 'check'}>
      {state.kind === 'saving' ? (
        <>{t('Enregistrement en cours…')}</>
      ) : state.kind === 'saved' ? (
        <>
          <Trans k='<1>Enregistré automatiquement</1> à <2>{at}</2> — chaque modification est conservée, il n’y a rien à valider.' vars={{ at: state.at }} parts={[(x) => <strong style={{ color: 'var(--teal-800)' }}>{x}</strong>, (x) => <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)' }}>{x}</span>]} /></>
      ) : (
        <>
          <Trans k='<1>Enregistrement automatique</1> — chaque modification est conservée dès que vous la faites, il n’y a rien à valider.' parts={[(x) => <strong style={{ color: 'var(--teal-800)' }}>{x}</strong>]} /></>
      )}
    </Notice>
  );
}

/** Une carte de la bande de sélection — vignette, nom, support, nombre d'éléments. */
function TemplateCard({ template, values, active, onPick, onRemove }: {
  template: LabelTemplate; values: LabelValues; active: boolean; onPick: () => void; onRemove: () => void;
}) {
  // ⚠️ LA VIGNETTE EST LE MÊME RENDU QUE LA PLANCHE, à une autre échelle : deux rendus finiraient
  //    par montrer deux étiquettes différentes, et c'est celle qu'on n'a pas regardée qui s'imprime.
  const scale = Math.min(150 / template.widthMm, 60 / template.heightMm);
  return (
    <div
      onClick={onPick}
      style={{
        position: 'relative', display: 'flex', flexDirection: 'column', gap: 7, padding: '14px 15px',
        border: `1.5px solid ${active ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-sm)',
        background: active ? 'var(--teal-100)' : 'var(--paper)', cursor: 'pointer', minHeight: 118,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        {/* ⚠️ LE NOM EST UN VRAI BOUTON : la carte se choisit au clic n'importe où, mais un `div` cliquable n'existe
            ni au clavier ni pour une aide technique — le gabarit ne pouvait se choisir qu'à la souris.
            `aria-pressed` dit lequel est en cours d'édition. */}
        <button
          type="button"
          aria-pressed={active}
          onClick={(e) => { e.stopPropagation(); onPick(); }}
          style={{
            flex: 1, minWidth: 0, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left',
            fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {template.name}
        </button>
        {template.locked && <Icon name="lock" size={13} />}
        {/* ⚠️ DEUX GESTES SOUS UNE MÊME AFFORDANCE, et l'étiquette les distingue : un standard se
            MASQUE (réversible), un personnalisé se SUPPRIME. Le même pictogramme pour les deux
            ferait croire qu'un gabarit livré peut se détruire.
            ⚠️ ET LE NOM DU BOUTON NOMME SON GABARIT : la carte est rendue une fois par gabarit, et « Masquer ce
            gabarit » répété sur chaque carte ne dit pas lequel (Forme IL). */}
        <button
          type="button"
          aria-label={template.locked ? t('Masquer « {name} »', { name: template.name }) : t('Supprimer « {name} »', { name: template.name })}
          title={template.locked ? t('Masquer ce gabarit — il restera rétablissable') : t('Supprimer ce gabarit')}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-500)', padding: 2 }}
        >
          <Icon name={template.locked ? 'minus' : 'trash'} size={14} />
        </button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
        <LabelPlate template={template} values={values} scale={scale} />
      </div>
      <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{mediaLabel(template)}</span>
      <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', fontStyle: 'italic' }}>
        {t('{n} élément', { n: template.elements.length })}
      </span>
    </div>
  );
}

/**
 * Le pointeur est-il parti assez loin pour que le geste soit un glisser ?
 * ⚠️ EN DEÇÀ, C'EST UN CLIC : un clic ou un double clic choisit un élément, il ne le déplace ni ne le redimensionne. Sans
 *    seuil, le moindre tremblement entre deux clics faisait sauter un élément d'un cran de grille, ou réduisait une boîte.
 */
function beyondDragThreshold(dx: number, dy: number): boolean {
  return Math.hypot(dx, dy) >= 3;
}

/**
 * La largeur, en mm, d'où part un redimensionnement.
 * ⚠️ UN TEXTE DE LARGEUR LIBRE N'A PAS DE LARGEUR DANS LE MODÈLE (`elementWidthMm` rend 0) : la poignée partait de zéro et
 *    ramenait la boîte à son minimum (3 mm) au premier mouvement — le champ « se réduisait complètement ». On part de la
 *    largeur que l'écran MONTRE, rembourrage ôté ; sans mise en page mesurable, de l'estimation du cœur sur le texte affiché.
 */
function startWidthMm(el: LabelTemplate['elements'][number], box: HTMLElement | null, zoom: number): number {
  const modelled = elementWidthMm(el);
  if (modelled > 0) return modelled;
  if (box && box.clientWidth > 0) {
    const css = getComputedStyle(box);
    const content = box.clientWidth - (Number.parseFloat(css.paddingLeft) || 0) - (Number.parseFloat(css.paddingRight) || 0);
    if (content > 0) return content / zoom;
  }
  return estimateTextWidthMm(box?.textContent ?? '', el.sizeMm, el.bold);
}

/**
 * Les réglages d'un gabarit libre — regroupés par NATURE, pas par ordre d'écriture.
 *
 * ⚠️ DEUX GROUPES, ET LA FRONTIÈRE EST RÉELLE. Le nom appartient au gabarit ; le support, le format
 * et les deux dimensions décrivent tous LE MÊME OBJET — le papier. Les mélanger obligeait l'œil à
 * faire l'aller-retour entre deux rangées pour une seule décision.
 *
 * ⚠️ L'ORDRE À L'INTÉRIEUR DU PAPIER VA DU PLUS GROS AU PLUS FIN : le **support** d'abord (il change
 * le vocabulaire — un rouleau Zebra et une planche Avery ne se choisissent pas dans la même liste),
 * puis le **format** qui pose les deux dimensions d'un coup, puis les **dimensions** elles-mêmes pour
 * le sur-mesure. Poser la largeur avant le format ferait régler à la main ce qu'un clic donne juste.
 */
function FormatPanel({ template, onOpenCatalog, onChange }: {
  template: LabelTemplate; onOpenCatalog: () => void; onChange: (tpl: LabelTemplate) => void;
}) {
  /** Une dimension de l'étiquette, bornée par ce qu'une imprimante sait tirer ; la découpe A4 se redéduit avec elle. */
  const dimension = (key: 'widthMm' | 'heightMm', label: string) => (
    <MmField
      label={label}
      value={template[key]}
      clamp={(v) => Math.max(LABEL_MIN_MM, Math.min(key === 'widthMm' ? LABEL_MAX_WIDTH_MM : LABEL_MAX_HEIGHT_MM, v))}
      onCommit={(v) => {
        const next = { ...template, [key]: v };
        onChange({ ...next, ...fitSheet(next.widthMm, next.heightMm) });
      }}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: 16, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)' }}>
      <FieldGroup title={t('Le gabarit')}>
        <Field
          label={t('Nom')}
          labelStyle="dense"
          defaultValue={template.name}
          hint={t('C’est sous ce nom que le gabarit se choisit à l’impression.')}
          onBlur={(e) => {
            const n = e.target.value.trim();
            if (n && n !== template.name) { onChange({ ...template, name: n }); return; }
            // ⚠️ UN NOM VIDÉ N'EST PAS ENREGISTRÉ, ET LA CASE NE RESTE PAS VIDE : elle afficherait un gabarit sans nom
            //    pendant que celui-ci garde le sien. Elle reprend le nom réel, et le dit.
            if (!n) {
              e.target.value = template.name;
              showToast(t('Un gabarit garde un nom — « {name} » est conservé.', { name: template.name }), 'warn');
            }
          }}
        />
      </FieldGroup>

      <div style={{ height: 1, background: 'var(--ink-100)' }} />

      <FieldGroup title={t('Le papier')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Le support et le format : la décision grossière, puis la référence matérielle. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0,1fr)', gap: 12, alignItems: 'end' }}>
            <div>
              <span style={LABEL_STYLE}>{t('Support')}</span>
              <Segmented
                label={t('Support')}
                options={[{ id: 'roll', label: t('Rouleau') }, { id: 'a4', label: t('Feuille A4') }]}
                value={template.kind}
                onChange={(v) => onChange({ ...template, kind: v as 'roll' | 'a4' })}
              />
            </div>
            <label>
              <span style={LABEL_STYLE}>{t('Format')}</span>
              <Button onClick={onOpenCatalog} icon="chevronD" style={{ width: '100%', justifyContent: 'space-between' }}>
                {formatLabel(template.widthMm, template.heightMm)}
              </Button>
            </label>
          </div>

          {/* Les dimensions : le sur-mesure, quand aucune référence ne convient. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,140px)) minmax(0,1fr)', gap: 12, alignItems: 'end' }}>
            {dimension('widthMm', t('Largeur — mm'))}
            {dimension('heightMm', t('Hauteur — mm'))}
            <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.45, paddingBottom: 9 }}>
              {template.kind === 'a4'
                // ⚠️ LA DÉCOUPE EST DÉDUITE, pas saisie : deux colonnes de trop et la dernière
                //    s'imprime hors de la feuille sans que rien ne le signale.
                ? t('{colonnes} × {lignes} par feuille — découpe déduite du format.', {
                  colonnes: t('{n} colonne', { n: template.cols ?? 1 }),
                  lignes: t('{n} ligne', { n: template.rows ?? 1 }),
                })
                : t('Imprimante thermique — une étiquette à la suite.')}
            </span>
          </div>
        </div>
      </FieldGroup>
    </div>
  );
}

/** L'intitulé d'un contrôle qui n'est pas un `Field` — même graisse, même encre, même gouttière. */
const LABEL_STYLE = { display: 'block' as const, fontSize: 'var(--t-meta)', fontWeight: 500 as const, color: 'var(--ink-500)', marginBottom: 4 };

/** La modale du catalogue de formats — matériels réels, groupés par support. */
function FormatCatalog({ template, onClose, onPick }: {
  template: LabelTemplate; onClose: () => void; onPick: (widthMm: number, heightMm: number, kind: 'roll' | 'a4') => void;
}) {
  const current = findCatalogFormat(template.widthMm, template.heightMm);
  return (
    <Modal open title={t('Format du papier')} icon="a4" onClose={onClose} width="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {LABEL_FORMAT_CATALOG.map((g) => (
          <div key={g.group}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)', display: 'block', marginBottom: 8 }}>
              {g.group}
            </span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 6 }}>
              {g.items.map((f) => {
                const active = current?.label === f.label && template.kind === g.kind;
                return (
                  <button
                    key={f.label}
                    type="button"
                    onClick={() => onPick(f.widthMm, f.heightMm, g.kind)}
                    style={{
                      textAlign: 'left', padding: '9px 11px', borderRadius: 'var(--r-xs)', cursor: 'pointer',
                      border: `1px solid ${active ? 'var(--teal-400)' : 'var(--ink-200)'}`,
                      background: active ? 'var(--teal-100)' : 'var(--paper)',
                    }}
                  >
                    <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)' }}>{f.label}</div>
                    <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{f.note}</div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
